// src/pages/OverviewTab.js
import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { AlertTriangle, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react'
import { MetricCard, Spinner, SectionTitle, TableWrap, Th, Td, Badge, formatRupees, formatTime, timeAgo } from '../components/ui'

export default function OverviewTab({ onTabSwitch }) {
  const [summary, setSummary]   = useState(null)
  const [health,  setHealth]    = useState([])
  const [dbStats, setDbStats]   = useState(null)
  const [loading, setLoading]   = useState(true)
  const [toggling, setToggling] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [s, h, d] = await Promise.all([
      supabase.rpc('get_admin_summary'),
      supabase.rpc('get_canteen_health_summary'),
      supabase.rpc('get_db_stats'),
    ])
    if (s.data)  setSummary(s.data)
    if (h.data)  setHealth(h.data)
    if (d.data)  setDbStats(d.data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const toggleCanteen = async (id, current) => {
    setToggling(id)
    await supabase.rpc('toggle_canteen_active', { p_canteen_id: id, p_is_active: !current })
    await load()
    setToggling(null)
  }

  if (loading) return <Spinner />

  const cron = dbStats?.cron_last_run
  const cronOk = cron && cron.status === 'succeeded'
  const cronStale = cron && (Date.now() - new Date(cron.start_time).getTime()) > 10 * 60 * 1000
  const dbPct = dbStats ? Math.round((dbStats.db_size_bytes / (500 * 1024 * 1024)) * 100) : 0

  return (
    <div className="space-y-6">

      {/* Failed webhook banner */}
      {summary?.unresolved_webhooks_today > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-semibold text-sm">
                {summary.unresolved_webhooks_today} payment webhook{summary.unresolved_webhooks_today > 1 ? 's' : ''} failed today — review required
              </p>
              <p className="text-red-400/60 text-xs mt-0.5">Payments may have been deducted without order confirmation</p>
            </div>
          </div>
          <button onClick={() => onTabSwitch('webhooks')}
            className="text-red-400 text-xs font-bold uppercase tracking-widest hover:text-red-300 whitespace-nowrap ml-4">
            Review →
          </button>
        </div>
      )}

      {/* Stuck refunds banner */}
      {summary?.initiated_refunds_stuck > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-amber-400 text-sm">
            {summary.initiated_refunds_stuck} refund{summary.initiated_refunds_stuck > 1 ? 's' : ''} stuck in INITIATED for 15+ min — manual retry needed
          </p>
          <button onClick={() => onTabSwitch('refunds')} className="text-amber-400 text-xs font-bold ml-auto">View →</button>
        </div>
      )}

      {/* Summary metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Orders today"   value={summary?.total_orders_today}   />
        <MetricCard label="Revenue today"  value={formatRupees(summary?.revenue_today)} accent="text-orange-400" />
        <MetricCard label="Failed today"   value={summary?.failed_today}  accent={summary?.failed_today > 0 ? 'text-red-400' : 'text-white'} />
        <MetricCard label="Open tickets"   value={summary?.open_tickets}  accent={summary?.open_tickets > 0 ? 'text-amber-400' : 'text-white'} />
      </div>

      {/* Per-canteen health */}
      <div>
        <SectionTitle>Canteen health</SectionTitle>
        <TableWrap>
          <thead>
            <tr>
              <Th>Canteen</Th>
              <Th right>Orders</Th>
              <Th right>Revenue</Th>
              <Th right>Failed</Th>
              <Th right>Tickets</Th>
              <Th>Last order</Th>
              <Th>Active</Th>
            </tr>
          </thead>
          <tbody>
            {health.map(row => (
              <tr key={row.canteen_id} className="hover:bg-white/[0.02] transition-colors">
                <Td><span className="font-medium text-white">{row.canteen_name}</span></Td>
                <Td right>{row.orders_today}</Td>
                <Td right>{formatRupees(row.revenue_today)}</Td>
                <Td right>
                  <span className={row.failed_today > 0 ? 'text-red-400 font-bold' : ''}>{row.failed_today}</span>
                </Td>
                <Td right>
                  <span className={row.open_tickets > 0 ? 'text-amber-400 font-bold' : ''}>{row.open_tickets}</span>
                </Td>
                <Td><span className="text-gray-500">{timeAgo(row.last_order_at)}</span></Td>
                <Td>
                  <button
                    onClick={() => toggleCanteen(row.canteen_id, row.is_active)}
                    disabled={toggling === row.canteen_id}
                    className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${row.is_active ? 'bg-green-500' : 'bg-gray-700'} ${toggling === row.canteen_id ? 'opacity-50' : ''}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${row.is_active ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </div>

      {/* System monitor row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

        {/* pg_cron status */}
        <div className="bg-[#1c1c1e] rounded-2xl p-4 border border-white/5">
          <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-3">Cron status</p>
          {dbStats?.cron_jobs ? (
            <div className="space-y-2">
              {dbStats.cron_jobs.map(job => (
                <div key={job.jobname} className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-200 text-xs font-medium">{job.jobname}</p>
                    <p className="text-gray-600 text-[10px]">{timeAgo(job.last_run)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {job.last_status === 'succeeded'
                      ? <CheckCircle className="w-4 h-4 text-green-400" />
                      : <XCircle className="w-4 h-4 text-red-400" />
                    }
                    <span className={`text-[10px] font-bold ${job.last_status === 'succeeded' ? 'text-green-400' : 'text-red-400'}`}>
                      {job.last_status || 'unknown'}
                    </span>
                  </div>
                </div>
              ))}
              {cronStale && (
                <p className="text-amber-400 text-[10px] mt-2 font-bold">⚠ Cron hasn't run in 10+ min</p>
              )}
            </div>
          ) : <p className="text-gray-600 text-xs">No cron data</p>}
        </div>

        {/* DB size */}
        <div className="bg-[#1c1c1e] rounded-2xl p-4 border border-white/5">
          <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-3">Database size</p>
          <p className="text-white text-xl font-bold mb-2">{dbStats?.db_size_pretty || '—'}</p>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${dbPct > 80 ? 'bg-red-500' : dbPct > 60 ? 'bg-amber-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(dbPct, 100)}%` }}
            />
          </div>
          <p className={`text-[10px] mt-1 font-medium ${dbPct > 80 ? 'text-red-400' : 'text-gray-500'}`}>
            {dbPct}% of 500MB free tier limit
          </p>
          {dbPct > 80 && <p className="text-red-400 text-[10px] font-bold mt-1">⚠ Approaching limit — upgrade soon</p>}
        </div>

        {/* DB connections */}
        <div className="bg-[#1c1c1e] rounded-2xl p-4 border border-white/5">
          <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-3">DB connections</p>
          <p className="text-white text-xl font-bold">{dbStats?.active_connections ?? '—'} <span className="text-gray-500 text-sm font-normal">active</span></p>
          <p className="text-gray-600 text-xs mt-1">{dbStats?.total_connections ?? '—'} total connections</p>
          <p className="text-gray-600 text-[10px] mt-2">PgBouncer pool: 15 (Nano) / 60 (Small)</p>
          {dbStats?.total_connections > 12 && (
            <p className="text-amber-400 text-[10px] font-bold mt-1">⚠ Approaching pool limit</p>
          )}
          <button onClick={load} className="mt-3 flex items-center gap-1 text-gray-600 hover:text-gray-400 text-[10px]">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>
    </div>
  )
}